import React from 'react';
import LockIcon from '@mui/icons-material/Lock';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { GlassCard, ProgressBarTrack, ProgressBarFill, Badge } from '../../styled/InsightNTutor';

function Pill({ children, color = '#3E8F93', bg = 'rgba(62,143,147,0.12)' }) {
  return (
    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, background: bg, color }}>
      {children}
    </span>
  );
}

// Shared, read-only presentational component for displaying a student's AI
// career-path prediction. Used by both the teacher "My Students" drawer and
// the school-admin students drawer. Generation is cron-only — this component
// never triggers generation, it only renders whatever state the backend returns.
//
// Props:
//   enabled        - boolean, whether the school's plan has careerPathEnabled
//   status         - 'not_eligible' | 'pending' | 'generating' | 'ready' | 'failed' | undefined
//   eligibility    - { monthsEnrolled, minMonthsRequired, gradedExamsCount, minExamsRequired }
//   snapshot       - normalized AI payload (only present when status === 'ready')
//   lastComputedAt - Date string
export default function CareerPathSummaryCard({ enabled, status, eligibility, snapshot, lastComputedAt }) {
  if (!enabled) {
    return (
      <GlassCard style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#78909c' }}>
          <LockIcon style={{ fontSize: '1.1rem' }} />
          <span style={{ fontSize: '0.85rem' }}>AI Career Path is not enabled on this school's current plan.</span>
        </div>
      </GlassCard>
    );
  }

  if (!status || status === 'not_eligible') {
    const months = eligibility?.monthsEnrolled ?? 0;
    const minMonths = eligibility?.minMonthsRequired ?? 18;
    const exams = eligibility?.gradedExamsCount ?? 0;
    const minExams = eligibility?.minExamsRequired ?? 8;
    return (
      <GlassCard style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <LockIcon style={{ fontSize: '1.1rem', color: '#90a4ae' }} />
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#455a64' }}>Not Yet Eligible</span>
        </div>
        <p style={{ fontSize: '0.8rem', color: '#78909c', marginBottom: 14 }}>
          Career path predictions unlock once a student has been enrolled for at least {minMonths} months
          and has {minExams}+ graded exams — this ensures the AI has enough real data to be useful.
        </p>
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#90a4ae', marginBottom: 4 }}>
            <span>Enrollment</span><span>{Math.min(months, minMonths)} / {minMonths} months</span>
          </div>
          <ProgressBarTrack height="6px">
            <ProgressBarFill progress={Math.min(100, Math.round((months / minMonths) * 100))} color="#5fbec5" />
          </ProgressBarTrack>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#90a4ae', marginBottom: 4 }}>
            <span>Graded Exams</span><span>{Math.min(exams, minExams)} / {minExams}</span>
          </div>
          <ProgressBarTrack height="6px">
            <ProgressBarFill progress={Math.min(100, Math.round((exams / minExams) * 100))} color="#5fbec5" />
          </ProgressBarTrack>
        </div>
      </GlassCard>
    );
  }

  if (status === 'pending' || status === 'generating') {
    return (
      <GlassCard style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3E8F93' }}>
          <HourglassEmptyIcon style={{ fontSize: '1.1rem' }} />
          <span style={{ fontSize: '0.85rem' }}>This student is eligible — predictions are being generated by our nightly AI process. Check back soon.</span>
        </div>
      </GlassCard>
    );
  }

  if (status === 'failed') {
    return (
      <GlassCard style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e65100' }}>
          <ErrorOutlineIcon style={{ fontSize: '1.1rem' }} />
          <span style={{ fontSize: '0.85rem' }}>We hit a snag generating this prediction. It will automatically retry on the next scheduled run.</span>
        </div>
      </GlassCard>
    );
  }

  // status === 'ready'
  if (!snapshot) return null;
  const paths = snapshot.topCareerPaths || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AutoAwesomeIcon style={{ fontSize: '1.1rem', color: '#5fbec5' }} />
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#455a64' }}>AI Career Path — Exploratory Possibilities</span>
        </div>
        {snapshot.confidenceLevel && (
          <Badge variant={snapshot.confidenceLevel === 'high' ? 'success' : undefined}>
            {snapshot.confidenceLevel} confidence
          </Badge>
        )}
      </div>

      {snapshot.narrativeSummary && (
        <p style={{ fontSize: '0.82rem', color: '#546e7a', margin: 0 }}>{snapshot.narrativeSummary}</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {paths.map((p, i) => (
          <GlassCard key={i} style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a6e73' }}>{p.title}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <Pill>{p.matchScore}% match</Pill>
                <Pill color="#2e7d32" bg="rgba(46,125,50,0.12)">AI-resilience {p.aiResilienceScore}%</Pill>
              </div>
            </div>
            {p.rationale && <p style={{ fontSize: '0.78rem', color: '#78909c', marginBottom: 8 }}>{p.rationale}</p>}
            {p.requiredSubjects?.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#546e7a', marginBottom: 4 }}>
                <strong>Subjects:</strong> {p.requiredSubjects.join(', ')}
              </div>
            )}
            {p.recommendedCourses?.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#546e7a' }}>
                <strong>Courses:</strong> {p.recommendedCourses.map(c => c.name).join(', ')}
              </div>
            )}
            {p.skillGaps?.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {p.skillGaps.map((s, si) => (
                  <span key={si} style={{ background: '#fff3e0', color: '#e65100', borderRadius: 10, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 600 }}>
                    {s.skill}: {s.current}/{s.required}
                  </span>
                ))}
              </div>
            )}
          </GlassCard>
        ))}
      </div>

      {(snapshot.strengthSubjects?.length > 0 || snapshot.growthAreas?.length > 0) && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {snapshot.strengthSubjects?.length > 0 && (
            <div style={{ flex: '1 1 200px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#2e7d32', marginBottom: 4 }}>Strengths</div>
              <div style={{ fontSize: '0.78rem', color: '#546e7a' }}>{snapshot.strengthSubjects.join(', ')}</div>
            </div>
          )}
          {snapshot.growthAreas?.length > 0 && (
            <div style={{ flex: '1 1 200px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e65100', marginBottom: 4 }}>Growth Areas</div>
              <div style={{ fontSize: '0.78rem', color: '#546e7a' }}>{snapshot.growthAreas.join(', ')}</div>
            </div>
          )}
        </div>
      )}

      {snapshot.milestones?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#455a64', marginBottom: 6 }}>Next Milestones</div>
          {snapshot.milestones.map((m, i) => (
            <div key={i} style={{ background: '#f7fbfc', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#37474f' }}>{m.label} {m.targetTermOrDate && <span style={{ color: '#90a4ae', fontWeight: 400 }}>({m.targetTermOrDate})</span>}</div>
              {m.description && <div style={{ fontSize: '0.75rem', color: '#78909c', marginTop: 2 }}>{m.description}</div>}
            </div>
          ))}
        </div>
      )}

      {snapshot.disclaimer && (
        <p style={{ fontSize: '0.72rem', color: '#90a4ae', fontStyle: 'italic', margin: 0 }}>{snapshot.disclaimer}</p>
      )}

      {lastComputedAt && (
        <div style={{ fontSize: '0.7rem', color: '#b0bec5' }}>
          Last updated {new Date(lastComputedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      )}
    </div>
  );
}
